// src/lib/materialRequests.js
//
// Canonical restock-request creation for catalog items — Issue #12.
// Single source of truth for the material_requests + material_request_items
// insert, in-app notification, and email dispatch that InventoryItemDetail.js,
// InventoryStockHome.js, and InventoryActionCatalog.js previously each
// re-implemented independently with divergent behavior. See ISSUES.md #12,
// #124, and the Session 63 close notes for the StockHome work_order_ref gap
// this consolidation fixed.
//
// ── Identifier separation (do not blend these) ──────────────────────────
// This function only ever writes work_order_ref. It never touches:
//   - request_number  — Stede-internal MR#, DB-generated on insert
//   - po_ref          — vendor purchase-order ref, set later by Purchasing
// These three identifiers are deliberately independent columns so Phase 2's
// executive/manager dashboards (planned late 2026 / early 2027) can filter
// and join on each one without cross-contamination. Do not fold any of them
// into `notes`, and any future caller of this function must pass an actual
// work order reference through workOrderRef — never a PO ref or an MR#.
//
// ── notes semantics (unified as of this consolidation) ──────────────────
// `notes` is genuine free-text user notes only — never an auto-generated
// audit string, and never a duplicate of workOrderRef. Two of the three
// legacy implementations (InventoryItemDetail.js, InventoryStockHome.js)
// used to write an auto-generated "Qty on hand: N" string here and also
// duplicate the WO# into material_request_items.notes; both patterns are
// deliberately dropped by this consolidation. If a future screen needs to
// surface "qty on hand at time of request," compute it from
// inventory_transactions/inventory_stock at display time rather than
// freezing it into this column.
//
// ── Off-catalog requests — NOT handled here ──────────────────────────────
// RequestMaterialsPage.js's off-catalog path (is_off_catalog,
// off_catalog_description, off_catalog_uom, off_catalog_vendor_name/id,
// photo_url, needed_by) is a separate, broader flow and was deliberately
// left out of this consolidation's scope — Stede already leads every direct
// competitor on that feature, so there was no urgency to fold it in here.
// If/when that page is migrated onto this shared utility, extend
// submitRestockRequest's params rather than writing a second parallel
// function. See Issue #12 close notes for the full reasoning.
//
// ── Validation is the caller's responsibility ────────────────────────────
// This function does not validate qty >= 1 or surface field-level error
// messages — each screen's UI owns its own inline validation/error state,
// since the wording and presentation legitimately differ per entry point.
// Callers should validate before calling and handle a thrown error from
// this function the same way they already handle their own Supabase calls.

import { supabase } from './supabase';
import { sendNotification, getRequestApproverIds, getPurchasingIds } from './notify';
import { orEveryoneWhoCanDecide } from './decisionMakers';
import { notifyRequestSubmitted, notifyRequestApproved } from './materialRequestEmails';

// #218. emailRestockRequest() has taken a qtyOnHand parameter since Session 55 and no
// call site has ever passed it, so the "Qty on hand" row has never rendered in a restock
// approval email — the template's null guard suppressed it silently rather than printing
// anything broken. Read here rather than threaded down from the calling screen so the
// number in the email is the one in the database at submit time, not whatever the UI was
// holding. Returns null when no stock row exists for the pair, which is a real state
// (#238 — a branch that has never received this item has no row yet), and null correctly
// suppresses the row again. A returned 0 is meaningful and must render: on a restock
// request, "Qty on hand: 0" is the whole point.
export async function getQtyOnHand(itemId, branchId) {
  if (!itemId || !branchId) return null;
  const { data, error } = await supabase
    .from('inventory_stock')
    .select('qty_on_hand')
    .eq('item_id', itemId)
    .eq('branch_id', branchId)
    .maybeSingle();
  if (error || !data) return null;
  return data.qty_on_hand ?? null;
}

// #396, Session 78n4. The department a request came from, stamped onto the
// request itself when it is raised.
//
// Read here rather than threaded down from the calling screen, and that is a
// deliberate trade of one tiny primary-key lookup for a whole class of silent
// failure. Requests are raised from four different screens; a screen that forgot
// to pass it would produce a request with no department, which does not error and
// does not look wrong — it just quietly routes to the branch manager forever.
// One lookup in one place cannot be forgotten by a fifth screen written later.
//
// Stamped rather than looked up live, because moving somebody between departments
// next year must not silently re-route the requests they raised last year. Oracle
// puts the cost centre on the requisition for exactly this reason.
//
// EXPORTED at Session 79.1 for #501, and the comment above turned out to be a
// prediction rather than a precaution. It says a screen that forgot to pass the
// department "would produce a request with no department, which does not error and
// does not look wrong — it just quietly routes to the branch manager forever."
// That is exactly what the off-catalog path in RequestMaterialsPage.js was doing:
// it writes its own insert and never called this. Measured at Session 79, same
// person ninety seconds apart — catalog request department 2, off-catalog null.
// The lookup was in one place and the fifth screen still could not be stopped from
// forgetting it, because it was private to this module.
export async function getRequesterDepartmentId(userId) {
  if (!userId) return null;
  const { data } = await supabase
    .from('users')
    .select('department_id')
    .eq('id', userId)
    .maybeSingle();
  return data?.department_id ?? null;
}

// #352, Session 79.22b.6.1g -- THE REQUEST AND EVERY LINE IN ONE STEP.
// Every screen used to insert the request and then its lines as two separate
// writes, so a failed second write left an empty "pending" request in the
// managers' inbox for good (RequestMaterialsPage.js tried to delete it again,
// #261, and that clean-up could fail too). create_material_request() does both
// in one database call - db/migrations/20261010_create_material_request_352.sql -
// so either all of it is saved or none of it is. It stamps who asked from the
// signed-in session and their department itself (#396, #501).
//
// lines: [{ item_id, qty_requested, notes?, is_off_catalog?, off_catalog_description?,
//           off_catalog_uom?, off_catalog_vendor_name?, off_catalog_vendor_id?,
//           photo_url?, needed_by? }]
export async function createRequestInOneStep({ branchId, workOrderRef, notes, lines }) {
  const { data, error } = await supabase.rpc('create_material_request', {
    p_branch_id: branchId,
    p_work_order_ref: workOrderRef || null,
    p_notes: notes || null,
    p_lines: lines,
  });
  if (error) throw error;
  return { id: data.request_id, request_number: data.request_number };
}

export async function submitRestockRequest({
  itemId,
  itemName,
  itemUom,
  branchId,
  branchName,
  qty,
  workOrderRef,
  notes,
  requestedBy, // { id, full_name }
}) {
  const parsedQty = parseInt(qty);
  // Still read here: it routes the alert below to the right approvers. The
  // request itself is stamped inside the database step.
  const departmentId = await getRequesterDepartmentId(requestedBy.id);

  const reqData = await createRequestInOneStep({
    branchId,
    workOrderRef,
    notes,
    lines: [{ item_id: itemId, qty_requested: parsedQty }],
  });

  // #396 - the two approvers for this request's department, not every manager
  // at the branch. Must agree with canApprove(); see notify.js's comment.
  // #650 -- a branch with nobody who can approve asks the admins and
  // executives instead of telling nobody.
  const { ids: managerIds } = await orEveryoneWhoCanDecide(await getRequestApproverIds(branchId, departmentId), branchId);
  await sendNotification(
    managerIds,
    `Restock requested: ${itemName}`,
    `${requestedBy.full_name} requested ${parsedQty} ${itemUom} of ${itemName} at ${branchName}. Request #${reqData.request_number}.${workOrderRef ? ' WO#: ' + workOrderRef : ''}`,
    'restock_request',
    'inventory',
    'requests_inbox',
    reqData.id
  );

  await notifyRequestSubmitted({
    branchId,
    branchName,
    requestNumber: reqData.request_number,
    requesterName: requestedBy.full_name,
    isOffCatalog: false,
    itemName,
    description: null,
    qty: parsedQty,
    uom: itemUom,
    qtyOnHand: await getQtyOnHand(itemId, branchId),
    vendorSuggestion: null,
    photoUrl: null,
    // #126 — the same reqData.id the sendNotification() above passes as link_id.
    requestId: reqData.id,
  });

  return { requestId: reqData.id, requestNumber: reqData.request_number };
}

// -- Multi-item restock request creation -- Issue #212 --
// Canonical multi-item counterpart to submitRestockRequest() above: one
// material_requests header, N material_request_items rows, one notification/
// email covering every item in the cart. All five #212 entry points route
// through this once they're multi-item aware (InventoryActionCatalog.js as
// of Session 74k; the remaining four entry points migrate in 74k2/74k3).
// submitRestockRequest() is NOT deprecated by this -- callers still on the
// single-item flow keep using it until they're migrated.
//
// Email portion verified against notify.js's emailRestockRequest() template
// (Session 74k): its "Qty requested" row interpolates qtyRequested directly
// into a template literal with no null guard, so passing null there would
// have rendered a literal "Qty requested: null" in the manager's inbox. For
// a multi-item cart this passes qty=items.length, uom='items', and folds
// the real per-item detail into itemName as a formatted list instead.
//
// Pre-existing, unrelated to #212: this call site never passes
// emailRestockRequest()'s 7th param, qtyOnHand -- the "Qty on hand" row has
// silently never rendered for any restock request email, single- or
// multi-item, since Session 55. Not fixed here -- flagging for ISSUES.md
// rather than expanding this session's scope.
export async function submitMultiItemRestockRequest({
  branchId,
  branchName,
  items, // [{ itemId, itemName, itemUom, qty }]
  workOrderRef,
  notes,
  requestedBy, // { id, full_name }
}) {
  const departmentId = await getRequesterDepartmentId(requestedBy.id);

  // #352 -- the request and every line of the cart in one step.
  const reqData = await createRequestInOneStep({
    branchId,
    workOrderRef,
    notes,
    lines: items.map(item => ({ item_id: item.itemId, qty_requested: parseInt(item.qty) })),
  });

  // #396 - the two approvers for this request's department, not every manager
  // at the branch. Must agree with canApprove(); see notify.js's comment.
  // #650 -- as above: nobody who can approve asks the admins and executives.
  const { ids: managerIds } = await orEveryoneWhoCanDecide(await getRequestApproverIds(branchId, departmentId), branchId);
  const isSingle = items.length === 1;
  const itemSummary = isSingle
    ? `${items[0].qty} ${items[0].itemUom} of ${items[0].itemName}`
    : items.map(i => `${i.qty} ${i.itemUom} ${i.itemName}`).join(', ');

  await sendNotification(
    managerIds,
    isSingle ? `Restock requested: ${items[0].itemName}` : `Restock requested: ${items.length} items`,
    `${requestedBy.full_name} requested ${itemSummary} at ${branchName}. Request #${reqData.request_number}.${workOrderRef ? ' WO#: ' + workOrderRef : ''}`,
    'restock_request',
    'inventory',
    'requests_inbox',
    reqData.id
  );

  await notifyRequestSubmitted({
    branchId,
    branchName,
    requestNumber: reqData.request_number,
    requesterName: requestedBy.full_name,
    isOffCatalog: false,
    itemName: isSingle ? items[0].itemName : itemSummary,
    description: null,
    qty: isSingle ? items[0].qty : items.length,
    uom: isSingle ? items[0].itemUom : 'items',
    // Single-item only, deliberately. A multi-item cart has one on-hand count per item and
    // the template's row is singular, so any one number would be attributed to the whole
    // request — the same category of error #230 just fixed on the history line. Multi-item
    // passes null and the row stays suppressed until the template carries per-item detail.
    qtyOnHand: isSingle ? await getQtyOnHand(items[0].itemId, branchId) : null,
    vendorSuggestion: null,
    photoUrl: null,
    // #126 — the same reqData.id the sendNotification() above passes as link_id.
    requestId: reqData.id,
    // Session 79.4, gate D10. The `itemName` above still carries the flattened
    // summary and that is deliberate: it remains the correct value for the
    // single-item case and a safe fallback. This is the real list, and the
    // template prefers it whenever there is more than one line. Every item in
    // this cart is a catalog pick, so none is off-catalog.
    lines: items.map(i => ({
      name: i.itemName,
      qty: i.qty,
      uom: i.itemUom,
      isOffCatalog: false,
    })),
  });

  return { requestId: reqData.id, requestNumber: reqData.request_number };
}

// -- Canonical approve/deny for pending material requests -- Issue #215/#216 --
// Single source of truth for accepting or rejecting a still-pending request.
// RequestsInboxPage.js is now the only caller -- Topbar.js's bell dropdown and
// NotificationOverlay.js's mobile dropdown no longer take action directly; a
// notification card only snoozes or deep-links in here. See ISSUES.md #215, #216.
//
// Both functions guard their UPDATE against the request having already moved
// past 'pending' -- a stale action returns { ok: false, stale: true } instead
// of silently overwriting real state. Both also clear every recipient's copy
// of the original restock_request/off_catalog_request notification, including
// an active snooze timer, so a decision made by one manager doesn't leave a
// stale actionable card sitting in another manager's queue.

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

function branchNameFor(id) {
  return BRANCHES.find(b => b.id === id)?.name || '-';
}

async function fetchRequestForDecision(requestId) {
  const { data, error } = await supabase
    .from('material_requests')
    .select('*, material_request_items(*, inventory_items(*))')
    .eq('id', requestId)
    .single();
  if (error || !data) return null;
  return data;
}

// -- Multi-item summaries for the decision half -- Issue #353 --
// submitMultiItemRestockRequest() above has built a correct multi-item summary on
// SUBMISSION since #212. The decision half never got one: approve and deny both
// read material_request_items[0] alone, so a three-line approval told the
// requester -- and purchasing, and the email -- about one line and silently
// dropped the rest. Same class as #230 on the Purchasing Queue, and #278 and #227
// before that.
//
// These read the joined rows fetchRequestForDecision() already returns, so no
// extra query. The format deliberately matches submitMultiItemRestockRequest()'s
// own -- "N uom Name", comma-joined -- so a requester sees the same words when
// they submit and when the answer comes back, rather than two dialects of one list.
//
// Off-catalog rows are why this cannot be a one-liner. material_request_items.item_id
// is nullable by design, so a line's name and uom come from inventory_items on a
// catalog row and from off_catalog_description/off_catalog_uom on an off-catalog
// one -- and a single request may legitimately hold both.

// Returns null rather than a placeholder when a name is missing, so callers keep
// the pre-#353 fallback to request_number on the single-item path unchanged.
// #509, Session 79.21c -- EXPORTED so the receive screen names an
// off-catalog line the same way every other surface does. It was private,
// and the receive screen would otherwise have grown its own copy of the
// is_off_catalog ternary -- which is #514 exactly, where a second
// implementation of a caller turned out to be kinder than the real one.
export function lineName(line) {
  return line?.is_off_catalog
    ? (line?.off_catalog_description || null)
    : (line?.inventory_items?.name || null);
}

export function lineUom(line) {
  return (line?.is_off_catalog ? line?.off_catalog_uom : line?.inventory_items?.uom) || '';
}

// Short reference for a notification title: the item's own name when there is
// exactly one line, a count when there is more than one. The single-line result is
// byte-identical to the `title` this replaced, which is what keeps every
// single-item message reading exactly as it did before #353.
function requestLabel(req) {
  const lines = req?.material_request_items || [];
  if (lines.length > 1) return `${lines.length} items`;
  return lineName(lines[0]) || req?.request_number;
}

// Every line, in the order they were requested.
function requestItemSummary(req) {
  const lines = req?.material_request_items || [];
  if (!lines.length) return req?.request_number || '';
  return lines
    .map(l => `${l.qty_requested} ${lineUom(l)} ${lineName(l) || 'Item'}`.replace(/\s+/g, ' ').trim())
    .join(', ');
}

async function clearRequestNotifications(requestId, eventTypes = ['restock_request', 'off_catalog_request']) {
  await supabase
    .from('notifications')
    .update({ read: true, snoozed_until: null })
    .eq('link_id', String(requestId))
    .in('event_type', eventTypes);
}

// #590 and #592, gate 391 D15-D17 -- WHEN A REQUEST ENDS, EVERY BELL THAT
// POINTED SOMEBODY AT IT IS CLEARED: the approvers' restock_request or
// off_catalog_request, Purchasing's purchase_ready_to_order, the executives'
// purchase_approval_needed. Approving already clears the first two; an ending
// has to clear all four, or Jennifer's "New in Needs Action" keeps pointing at a
// request that is gone.
const ENDED_REQUEST_BELLS = [
  'restock_request', 'off_catalog_request', 'purchase_ready_to_order', 'purchase_approval_needed',
];

async function adminExecutiveIds() {
  const { data } = await supabase
    .from('users').select('id').in('role', ['admin', 'executive']).eq('is_active', true);
  return (data || []).map(u => u.id);
}

// #590's purchase half, gate 391 D13-D16, Session 79.21b -- THE ASKER TAKES A
// PURCHASE REQUEST BACK, with a reason, until Purchasing orders it.
//
// withdraw_material_request() takes the row FOR UPDATE, refuses anybody but the
// asker and anything already ordered or ended, and RETURNS THE STATUS IT ENDED
// FROM - which is how this knows whom to tell (D16), and why it does not trust
// error === null (#504).
export async function withdrawMaterialRequest({ request, reason, askerName = null }) {
  const why = String(reason || '').trim();
  if (!why) return { ok: false, error: new Error('Say why you are withdrawing it.') };
  const { data: was, error } = await supabase.rpc('withdraw_material_request', {
    p_request_id: request.id,
    p_reason: why,
  });
  if (error) return { ok: false, error };
  if (!['pending', 'approved', 'pending_purchase_approval'].includes(was)) {
    return { ok: false, error: new Error('That did not save. Nothing was withdrawn — try again.') };
  }

  await clearRequestNotifications(request.id, ENDED_REQUEST_BELLS);

  const who = askerName || 'The person who asked';
  const num = request.request_number || 'a request';
  if (was === 'pending') {
    // Waiting on approval: the approvers it was sent to (the same lookup
    // submitRestockRequest made), on the Inventory chip.
    const approvers = await getRequestApproverIds(request.branch_id, request.department_id ?? null);
    const audience = approvers.filter(id => String(id) !== String(request.requested_by));
    if (audience.length) {
      await sendNotification(
        audience, 'Request withdrawn', `${who} withdrew ${num}: “${why}”`,
        'material_request_withdrawn', 'inventory', 'requests_inbox', null,
      );
    }
  } else {
    // Waiting on Purchasing - and on an executive's sign-off too, when it was
    // over the limit. Purchasing chip, one type for one chip (Finding 5).
    const people = [...(await getPurchasingIds())];
    if (was === 'pending_purchase_approval') people.push(...(await adminExecutiveIds()));
    const audience = [...new Set(people.filter(Boolean).map(String))]
      .filter(id => id !== String(request.requested_by));
    if (audience.length) {
      await sendNotification(
        audience, 'Request withdrawn', `${who} withdrew ${num} before it was ordered: “${why}”`,
        'purchase_request_withdrawn', 'purchasing', 'purchasing_queue', null,
      );
    }
  }
  return { ok: true, was };
}

// #642, Session 79.22b -- THE STATUSES PURCHASING MAY CANCEL FROM, IN ONE PLACE.
// cancel_material_request() allows exactly these three, and every screen offered
// the control for the first alone -- so an order already placed could not be
// canceled while the asker's own card said "ask Purchasing to cancel it". The
// screens read this rather than restating it: three copies of one rule drift in
// the copy nobody is editing (locked rule 8). It is deliberately NOT `isPending`,
// which also decides the Needs-action tab and its styling.
export const CANCELLABLE_STATUSES = ['approved', 'pending_purchase_approval', 'ordered'];

export function canCancelFromHere(req) {
  return CANCELLABLE_STATUSES.includes(req?.status);
}

// #592, gate 391 D17, Session 79.21b -- PURCHASING'S CANCEL ASKS WHY AND
// KEEPS WHO AND WHEN. It replaces a bare status write from the browser that kept
// no reason, no name, and told nobody. Tells the person who asked and the
// manager who approved it, carrying the reason, and My requests shows it like a
// denial. Switchable in preferences like material_request_denied.
export async function cancelMaterialRequest({ request, reason, cancellerName = null, cancellerId = null }) {
  const why = String(reason || '').trim();
  if (!why) return { ok: false, error: new Error('Say why you are canceling it.') };
  const { data: was, error } = await supabase.rpc('cancel_material_request', {
    p_request_id: request.id,
    p_reason: why,
  });
  if (error) return { ok: false, error };
  if (!CANCELLABLE_STATUSES.includes(was)) {
    return { ok: false, error: new Error('That did not save. Nothing was canceled — try again.') };
  }

  await clearRequestNotifications(request.id, ENDED_REQUEST_BELLS);

  const by = cancellerName || 'Purchasing';
  const num = request.request_number || 'a request';
  // Nobody is told about their own action -- the same rule the withdraw path
  // keeps. An admin who approved a request and then cancels it from the
  // Purchasing Queue would otherwise get a bell saying they had canceled it.
  const notMe = id => id && String(id) !== String(cancellerId);
  if (notMe(request.requested_by)) {
    await sendNotification(
      [request.requested_by], 'Your request was canceled',
      `${num}, by ${by} in Purchasing: “${why}”`,
      'material_request_cancelled', 'inventory', 'my_requests', request.id,
    );
  }
  // THE APPROVER'S BELL CARRIES NO LINK, on purpose. Gate 391 B4 sent it to My
  // requests, which only ever shows a person's OWN requests, so the manager
  // would land on a page without it -- and no screen shows a manager a canceled
  // request they approved. The message says everything; a bell with no link is
  // honest where a link to the wrong page is not (F1). Found proving P5, #592.
  if (notMe(request.approved_by) && String(request.approved_by) !== String(request.requested_by)) {
    await sendNotification(
      [request.approved_by], 'A request you approved was canceled',
      `${num}, by ${by} in Purchasing: “${why}”`,
      'material_request_cancelled', 'inventory', null, null,
    );
  }
  return { ok: true, was };
}

// #391, Session 78n — `approverId` is new, and it exists to fill two columns
// that have been on this table since #210 and were never written by this path.
//
// `approved_by` and `approved_at` were added by the purchase-approval threshold
// migration (20260812) and are written ONLY by the executive's over-threshold
// decision in PurchasingQueuePage.js. The manager's approve — the ordinary one,
// the one that happens on every request — set status and nothing else. So Stede
// has never been able to say who approved a material request, or when.
//
// Found while building My requests: its tracker wants to name the approver at
// the stage that is waiting, and there was no name to read. The page derives
// the WAITING approver from the branch-manager lookup instead, which is correct
// for a pending request in any case — nobody has approved it yet. But an
// already-approved request should be able to say who did it, and from today it
// can. Approved by Colby at 78n as its own decision rather than folded in.
//
// No schema change: both columns already exist. This is backfill-forward only —
// every request approved before today keeps a null, and the UI must not read
// these as guaranteed.
export async function submitApproveRequest({ requestId, approverName, approverId }) {
  const req = await fetchRequestForDecision(requestId);
  if (!req) return { ok: false, notFound: true };

  const { data: updated, error: updateErr } = await supabase
    .from('material_requests')
    .update({
      status: 'approved',
      updated_at: new Date().toISOString(),
      // Null rather than absent when the caller has no id: writing a wrong
      // approver is worse than writing none, and `approved_by` is an FK into
      // public.users that a bad value would fail on anyway.
      approved_by: approverId || null,
      approved_at: new Date().toISOString(),
    })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select();
  if (updateErr) return { ok: false, error: updateErr };
  if (!updated || updated.length === 0) return { ok: false, stale: true, req };

  await clearRequestNotifications(requestId);

  // #353. Every message below described material_request_items[0] and nothing else.
  const lines = req.material_request_items || [];
  const isMulti = lines.length > 1;
  const firstItem = lines[0];
  // On a multi-line request "off-catalog" means AT LEAST ONE line still needs
  // sourcing, which is the operationally correct signal for purchasing: the email's
  // banner asks them to establish pricing, and one unpriced line is enough to need
  // it. Reading it off line one alone would hide an off-catalog line sitting second.
  const isOffCatalog = isMulti
    ? lines.some(l => !!l?.is_off_catalog)
    : !!firstItem?.is_off_catalog;
  const label = requestLabel(req);
  // Single-item keeps the exact wording it had before #353; only a real multi-item
  // request gets the expanded list, so there is no cosmetic churn on the common case.
  const detail = isMulti ? requestItemSummary(req) : label;

  await notifyRequestApproved({
    requestNumber: req.request_number,
    branchName: branchNameFor(req.branch_id),
    approverName,
    isOffCatalog,
    // Multi-item folds the whole list into BOTH itemName and description so the
    // template renders the same summary whichever side of its own isOffCatalog
    // ternary it takes. That is deliberate rather than redundant: relying on the
    // template's `description || itemName` fallback would start silently dropping
    // every line but one if that ternary were ever rearranged.
    //
    // qty/uom follow the pattern submitMultiItemRestockRequest() established and
    // documented above -- emailRequestApprovedForPurchasing() interpolates qty into
    // "Qty needed" with no null guard, so a count plus the literal 'items' is what
    // keeps that row readable when the quantities differ per line.
    itemName: isMulti ? detail : (isOffCatalog ? null : (firstItem?.inventory_items?.name || null)),
    description: isMulti ? detail : (isOffCatalog ? (firstItem?.off_catalog_description || null) : null),
    qty: isMulti ? lines.length : firstItem?.qty_requested,
    uom: isMulti ? 'items' : (isOffCatalog ? firstItem?.off_catalog_uom : firstItem?.inventory_items?.uom),
    // #126 — purchasing's email now opens the Purchasing Queue on this request,
    // the same destination #306 gave purchasing's in-app card below.
    requestId: req.id,
    // Session 79.4, gate D10 and D8. Unlike the submission cart, an approved
    // request CAN mix catalog and off-catalog lines -- `isOffCatalog` above is
    // already lines.some(...) for exactly that reason -- so each line carries its
    // own flag and the table tags the ones purchasing still has to source.
    // lineName()/lineUom() are reused rather than re-derived so the table and the
    // notification text cannot drift apart.
    lines: lines.map(l => ({
      name: lineName(l),
      qty: l?.qty_requested,
      uom: lineUom(l),
      isOffCatalog: !!l?.is_off_catalog,
    })),
  });

  // #306. Purchasing's in-app half of the same event the email above already covers.
  // Before this, approval sent purchasing an email and nothing else: the only
  // sendNotification() here went to req.requested_by, so the inventory-chipped
  // "your request was approved" card is the REQUESTER'S and purchasing had no row,
  // no badge and no toast. Jennifer runs purchasing from Houston off the Purchasing
  // Queue's Needs Action list, and from Sep 1 email would have been her only signal.
  //
  // Its own event_type, deliberately. Reusing material_request_approved with
  // domain: 'purchasing' for a different recipient is Finding 5's exact shape in
  // NOTIFICATION_VOCABULARY.md, logged as a defect and resolved at Session 58.
  // purchase_ready_to_order requires db/migrations/20260823_notifications_purchase_ready_to_order.sql
  // to be applied FIRST -- this call is awaited, so a value the CHECK rejects fails
  // the approval action itself, not just the notification.
  //
  // Recipient set matches getPurchasingEmails() above rather than widening to
  // getPurchasingQueueActionEmails(): #306 is a channel gap, not an audience gap, so
  // the in-app row goes to exactly who the email already goes to.
  // WORDING IS DELIBERATE AND WAS CHANGED ONCE. The first draft read "Ready to
  // order: {item}", which collides with purchase_approval_granted's "Approved -
  // ready to place the order" (PurchasingQueuePage.js, #256) -- same recipient set,
  // same domain, same link_to, and BOTH can fire on one request: this one when a
  // manager approves it, that one later if the order trips #210's dollar threshold
  // and an executive clears it. Two near-identical sentences in one inbox with no
  // way to tell them apart. They are different events, so the fix is wording:
  // this one means NEW WORK ARRIVED (pick a vendor, place the order), that one
  // means BLOCKED WORK UNBLOCKED (vendor already chosen, go place it). Naming the
  // queue stage is what separates them, because it also says where to go.
  // The event_type keeps the name purchase_ready_to_order -- it matches the email
  // subject already sent for this event, and the constraint is already applied.
  const purchasingIds = await getPurchasingIds();
  await sendNotification(
    purchasingIds,
    `New in Needs Action: ${label}`,
    `${approverName} approved ${req.request_number} for ${branchNameFor(req.branch_id)} \u2014 ${detail}. Ready to source and order.`,
    'purchase_ready_to_order', 'purchasing', 'purchasing_queue', requestId
  );

  if (req.requested_by) {
    await sendNotification(
      [req.requested_by],
      'Your material request was approved',
      `${detail} \u2014 ${req.request_number} approved for ${branchNameFor(req.branch_id)}.`,
      // #391 D11, Session 78n. Retargeted from `requests_inbox`. This is one of
      // the two notifications Stede sends to the REQUESTER, and it was opening
      // the branch's approval queue -- the shortest path to the wrong screen in
      // the app, and a door into it that survived even after the bar slot was
      // declined at 78j. The other is the denial below.
      'material_request_approved', 'inventory', 'my_requests', requestId
    );
  }

  return { ok: true, req };
}

// #397, Session 78n4 — reasonId, reasonLabel and note added.
//
// reasonId is REQUIRED by the sheet that calls this, and the requirement lives
// there rather than here on purpose: this function is also the path a future
// screen might call, and a thrown error is a worse answer than a validated form.
// What this function guarantees instead is that whatever it is given, it stores
// BOTH the id and the label.
//
// Why both, since a later session will want to delete one: the id makes denials
// countable — "how often is it already in stock elsewhere" is the question that
// justifies #371's inter-branch transfers. The label freezes what the reason
// SAID when it was chosen, so renaming it next year cannot rewrite what a
// manager told somebody last year. Same pattern as unit_cost_at_transaction.
// That is history, not the copied-key weakness this session removed from
// users.department — see the migration header, which argues it at length.
//
// `denial_reason` keeps its existing meaning as free text and becomes the NOTE.
// It was already free text on the one path that wrote it, the executive's
// over-threshold denial, so nothing is migrated and nothing is reinterpreted.
export async function submitDenyRequest({ requestId, reasonId = null, reasonLabel = null, note = null }) {
  const req = await fetchRequestForDecision(requestId);
  if (!req) return { ok: false, notFound: true };

  const { data: updated, error: updateErr } = await supabase
    .from('material_requests')
    .update({
      status: 'denied',
      denial_reason_id: reasonId,
      denial_reason_label: reasonLabel,
      denial_reason: note,
      updated_at: new Date().toISOString(),
    })
    .eq('id', requestId)
    .eq('status', 'pending')
    .select();
  if (updateErr) return { ok: false, error: updateErr };
  if (!updated || updated.length === 0) return { ok: false, stale: true, req };

  await clearRequestNotifications(requestId);

  // #353, same defect as submitApproveRequest() above. Deny sends no email, so this
  // is the one message a requester gets -- naming one line of three is worse here
  // than on approval, because there is no follow-up email to correct the record.
  const isMulti = (req.material_request_items || []).length > 1;
  const detail = isMulti ? requestItemSummary(req) : requestLabel(req);

  if (req.requested_by) {
    await sendNotification(
      [req.requested_by],
      'Your material request was denied',
      // #397 \u2014 the reason goes IN the notification, not only on the screen it
      // links to. This is the only message a denied requester receives, and a
      // notification that says "denied" and makes them open a page to find out
      // why is the same dead end with an extra tap in front of it.
      `${detail} \u2014 ${req.request_number} was denied for ${branchNameFor(req.branch_id)}.`
        + (reasonLabel ? ` Reason: ${reasonLabel}.` : '')
        + (note ? ` ${note}` : ''),
      // #391 D11, Session 78n. Retargeted from `requests_inbox`, and this is the
      // more consequential of the two: deny sends no email, so this notification
      // is the ONLY message a requester gets, and its link was opening a screen
      // whose fetch (`.in('status', ['pending','ordered'])`) cannot return a
      // denied request at all. The link led to a page guaranteed not to show
      // the thing the notification was about.
      'material_request_denied', 'inventory', 'my_requests', requestId
    );
  }

  return { ok: true, req };
}
